import {Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Observable}                                                         from 'rxjs';
import {ApElementOxydTypeEnum, IGridData, MapViewMode}                      from '../../../ap-interface';
import {
  ApDynGridColumnConfigBase
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridGroupColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerWizardBreadcrumbsStepper,
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardExport
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {SortDescriptor}                                                     from '@progress/kendo-data-query';
import {
  ApDynGridButtonColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-button-column-config';
import {APP_CONFIGURATION}                                                  from '../../../ap-core/config';
import {
  SettingsStore
}                                                                           from '../../../stores/base-data/settings.store';
import {
  FertilizerOrganicStore
}                                                                           from '../../../stores/base-data/fertilizer.organic.store';
import {filter, map}                                                        from 'rxjs/operators';
import {GetElementOxidCalculatePipe, GetRoundNumericPipe}                   from '../../../ap-utils';
import {MapViewStore}                                                       from '../../../stores/layout/mapview.store';
import {
  TranslationStore
}                                                                           from '../../../stores/translation/translation.store';
import {
  ApDynGridTemplateColumnConfig
}                                                                           from '../../../ap-dyngrids/config/ap-dyn-grid-template-column-config';
import IFertilizerOrganic = Data.BaseData.IFertilizerOrg;

interface IFertilizerOrganicGridData extends IGridData<IFertilizerOrganic> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
  CaOValue: any;
  KValue: any;
  MGValue: any;
  NValue: any;
  PValue: any;
  Efficiency: any;
  DMPercent: any;
  Liquid: any;
  Density: any;
}

@Component({
  selector: 'ap-grid-style',
  template: `
    <ap-dyncomponent (edit)="edit($event)"
                     [caption]="'Global__Sample_Grid'"
                     [columns]="columns"
                     [exportFileName]="'test'"
                     [headerIcon]="'ap-basedata-icon'"
                     [items]="this.items$ | async"
                     [loading$]="fertilizerOrganicLoading$"
                     [pager]="pager">
    </ap-dyncomponent>
    <ng-template #header let-columnContext="columnContext">
      <h4>{{ columnContext.name }}</h4>
      <img src="../../../../assets/img/lkw_weiss_klein-l.png" alt="">
    </ng-template>
    <ng-template #cell let-columnContext="columnContext" let-dataItem="dataItem">
      <span>
        <p>{{ columnContext.name }}</p>
        <p>Name {{ dataItem.SourceItem.Prodname }}</p>
        <kendo-dropdownlist [data]="columnContext.desc$ | async"
                            [value]="dataItem.SourceItem.Prodname"
                            [style.width]="'100px'"
        ></kendo-dropdownlist>
      </span>
    </ng-template>
  `,
})
export class ApGridStyleComponent implements OnInit, OnDestroy {
  @ViewChild('header', {static: true}) headerTemplate: TemplateRef<any>;
  @ViewChild('cell', {static: true}) cellTemplate: TemplateRef<any>;

  public subscriptions = [];
  public columns: ApDynGridColumnConfigBase[];
  public pager: any;
  public settings;
  public sort: SortDescriptor[];
  public mapViewModes = MapViewMode;
  public mapViewState$: Observable<MapViewMode>;
  fertilizerOrganicLoading$ = this.store.Listen(s => s.loading).asObservable();
  items$ = this.store.Listen(s => s.data).pipe(
    filter(d => !!d),
    map(pp => pp.Convert<IFertilizerOrganicGridData>((organic: IFertilizerOrganic) => {
      const kValue = this._elementOxidCalculate(organic.ContentK, ApElementOxydTypeEnum.K);
      const mgValue = this._elementOxidCalculate(organic.ContentMg, ApElementOxydTypeEnum.Mg);
      const pValue = this._elementOxidCalculate(organic.ContentP, ApElementOxydTypeEnum.P);
      const isLiquid = organic.IsLiquid ? this.translationStore.FindTranslationForSelectedLanguage('Global__Aggregate_State_Liquid')
        : this.translationStore.FindTranslationForSelectedLanguage('Global__Aggregate_State_Solid');
      return {
        Id: organic.Id,
        SourceItem: organic,
        Status: organic.Selectable ? 1 : -1,
        StatusClass: organic.Selectable ? 'ap-status-green' : 'ap-status-red',
        StatusDesc: organic.Selectable ? 'Global__Active' : 'Global__Inactive',
        CaOValue: organic.ContentCaO,
        KValue: kValue,
        MGValue: mgValue,
        NValue: organic.ContentN,
        PValue: pValue,
        Efficiency: organic.Efficiency,
        DMPercent: organic.DMPercent,
        Liquid: isLiquid,
        Density: organic.Density
      };
    })));
  private onBack = new EventEmitter<any>();
  private onClick = new EventEmitter<any>();

  constructor(private store: FertilizerOrganicStore,
              private mapViewStore: MapViewStore,
              private settingsStore: SettingsStore,
              private translationStore: TranslationStore,
              private roundNumericPipe: GetRoundNumericPipe,
              private elementOxideCalculate: GetElementOxidCalculatePipe) {
    this.mapViewStore.hideMapView();
  }

  ngOnInit(): void {
    this.getDataFromStore();
    this.buildGrid();

    this.onClick.subscribe($event => {
      console.log($event);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  getDataFromStore(): void {
    this.subscriptions.push(this.settingsStore.FirstSetting$.subscribe(s => this.settings = s));
    this.mapViewState$ = this.mapViewStore.Listen(s => s.mode);
  }

  buildGrid(): void {
    this.columns = [
      new ApDynGridStatusColumnConfig({
        field: 'Selectable', class: 'SelectableClass', description: 'SelectableDesc',
        sortIndex: 1, filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Shortname', title: 'Global__Token', sortIndex: 2, sortDesc: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'SourceItem.Description', title: 'Global__Name', hide: true
      }),
      new ApDynGridGroupColumnConfig({
        headerStyle: {
          'text-align': 'center'
        },
        style: {border: '1px solid #2c3237'},
        title: 'Global__Contents_Percent',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            field: 'CaOValue', title: 'Global__NutrientCaO',
            pipes: [
              {
                pipe: this.roundNumericPipe,
                args: [this.settings.DigitsAfterDecimalPoint]
              }
            ]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'KValue', title: 'Global__NutrientK',
            pipes: [
              {
                pipe: this.roundNumericPipe,
                args: [this.settings.DigitsAfterDecimalPoint]
              }
            ]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'MGValue', title: 'Global__NutrientMg',
            pipes: [
              {
                pipe: this.roundNumericPipe,
                args: [this.settings.DigitsAfterDecimalPoint]
              }
            ]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'NValue', title: 'Global__NutrientN',
            pipes: [
              {
                pipe: this.roundNumericPipe,
                args: [this.settings.DigitsAfterDecimalPoint]
              }
            ]
          }),
          new ApDynGridPropertyColumnConfig({
            field: 'PValue', title: 'Global__NutrientP',
            pipes: [
              {
                pipe: this.roundNumericPipe,
                args: [this.settings.DigitsAfterDecimalPoint]
              }
            ]
          }),
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Efficiency', title: 'Settings__Lbl_NEffectiveness', hide: true,
        pipes: [
          {
            pipe: this.roundNumericPipe,
            args: [this.settings.DigitsAfterDecimalPoint]
          }
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'DMPercent', title: 'Global__Drymatter', hide: true,
        pipes: [
          {
            pipe: this.roundNumericPipe,
            args: [this.settings.DigitsAfterDecimalPoint]
          }
        ]
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Liquid', title: 'Global__Aggregate', hide: true, filterable: true
      }),
      new ApDynGridPropertyColumnConfig({
        field: 'Density', title: 'Global__Density', hide: true,
        pipes: [
          {
            pipe: this.roundNumericPipe,
            args: [this.settings.DigitsAfterDecimalPoint]
          }
        ]
      }),
      new ApDynGridTemplateColumnConfig({
        header: this.headerTemplate,
        template: this.cellTemplate,
        context: {
          name: 'COLUMN CONTEXT',
          desc$: this.items$.pipe(map((items) => items.map((item) => item.SourceItem.Prodname)))
        }
      }),
      new ApDynGridButtonColumnConfig({
        callback: this.onClick, imageUrl: APP_CONFIGURATION.ButtonZoomToMapImage
      }),
      new ApDynGridEditColumnConfig({}),
    ];

    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton('Global_Back_With_Arrow', this.onBack),
      new ApDynGridPagerWizardBreadcrumbsStepper(['Admin_Pages__Overview', 'Global__Details'], 1),
      new ApDynGridPagerWizardExport(),
    );
  }

  public edit(dataItem): void {
    console.log(dataItem);
  }

  private _elementOxidCalculate(value: number, unit: ApElementOxydTypeEnum): number {
    return this.elementOxideCalculate.transform(
      value, unit, this.settings.UnitElementOxyd
    );
  }
}
